/**
 * Storage abstraction.
 *
 * Every provider (IndexedDB, LocalStorage, Tableau Settings, REST→SQL/Snowflake)
 * implements this identical async contract, so the rest of the app is fully
 * storage-agnostic. Providers must be JSON-safe and resilient: a failed read
 * should reject, never corrupt local state.
 */

import type {
  Combination,
  ExtensionConfig,
  Favorite,
  UsageEvent,
} from "@/types";

export interface StorageProvider {
  /** Provider id, for diagnostics and the admin screen. */
  readonly kind: string;
  /** One-time async setup (open DB, ping API, etc.). */
  init(): Promise<void>;

  // ── Combinations ──────────────────────────────────────────────────────────
  getCombinations(): Promise<Combination[]>;
  /** Insert or update by id. */
  putCombination(combination: Combination): Promise<void>;
  deleteCombination(id: string): Promise<void>;

  // ── Usage events (append-only audit log) ────────────────────────────────────
  getUsageEvents(): Promise<UsageEvent[]>;
  appendUsageEvent(event: UsageEvent): Promise<void>;

  // ── Favorites ───────────────────────────────────────────────────────────────
  getFavorites(): Promise<Favorite[]>;
  putFavorite(favorite: Favorite): Promise<void>;
  deleteFavorite(id: string): Promise<void>;

  // ── Config ────────────────────────────────────────────────────────────────
  getConfig(): Promise<Partial<ExtensionConfig> | null>;
  putConfig(config: ExtensionConfig): Promise<void>;

  /** Optional: wipe everything (admin "reset analytics"). */
  clearAll?(): Promise<void>;
}

export const STORAGE_KEYS = {
  combinations: "spc:combinations",
  events: "spc:events",
  favorites: "spc:favorites",
  config: "spc:config",
} as const;
